import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import BlogHome from '../pages/home/BlogHome';

import { useAuth } from '../context/AuthContext';
import { applyPageMeta, metaForPath } from '../utilityFunction/pageMeta';

// Every page except the home page is downloaded only when someone first opens it, so visitors
// don't load the admin tools, checkout (and Stripe's script) and so on up front.
const RecipesComponentMain = lazy(() => import('../pages/single/blogRecipes'));
const Write = lazy(() => import('../pages/write/blogWrite'));
const Settings = lazy(() => import('../pages/settings/Setting'));
const SignUp = lazy(() => import('../pages/signup/signup'));
const Login = lazy(() => import('../pages/login/login'));
const ForgotPassword = lazy(() => import('../pages/login/forgotPassword'));
const ResetPassword = lazy(() => import('../pages/login/resetPassword'));
const DynamicPosts = lazy(() => import('../pages/dynamicPosts/DynamicPosts'));
const BodyWashComponentMain = lazy(() => import('../pages/single/blogBodyWash'));
const FaceCreamComponentMain = lazy(() => import('../pages/single/blogFaceCream'));
const ScrubComponentMain = lazy(() => import('../pages/single/blogScrubs'));
const EditPost = lazy(() => import('../pages/editposts/editpost'));
const Shop = lazy(() => import('../pages/shop/shopPage'));
const PaymentForm = lazy(() => import('../pages/stripePage/checkoutPage'));
const SuccessPayement = lazy(() => import('../pages/successpage/successPayement'));
const DynamicShop = lazy(() => import('../pages/shop/dynamicShop'));
const EditShop = lazy(() => import('../pages/editshop/editshop'));
const MyOrders = lazy(() => import('../pages/myorders/myOrders'));
const About = lazy(() => import('../pages/about/About'));
const Contact = lazy(() => import('../pages/contact/Contact'));
const LegalPage = lazy(() => import('../pages/legal/LegalPage'));

// Shown while a page's code downloads, or while the login check is still running.
function PageLoading() {
    return <div className="page-loading" role="status">Loading…</div>;
}

// Sets the tab title, description and canonical address for each page (pageMeta.js). It sits
// before the pages, so React runs it first and a product or post page can then put in its own.
function RouteMeta() {
    const { pathname } = useLocation();
    useEffect(() => {
        applyPageMeta(metaForPath(pathname));
    }, [pathname]);
    return null;
}

export default function RoutesBlog() {
    const { isLoggedIn, currentUserRole, authChecked } = useAuth();
    const isAdmin = isLoggedIn && currentUserRole === 'admin';

    // Public pages render straight away. Account and admin pages wait for the login check,
    // so opening one directly doesn't flash "not found" before it knows who you are.
    const account = (page) => (!authChecked ? <PageLoading /> : page);

    return(
        <>
        <RouteMeta />
        <Suspense fallback={<PageLoading />}>
            <Routes>
                <Route path="/" element={<BlogHome />} />
                { !isLoggedIn && <Route path="/login" element={<Login />} />}
                {/* Password reset: ask for an email, then the emailed link opens the second page. */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/Recipes" element={<RecipesComponentMain />} />
                <Route path="/BodyWash" element={<BodyWashComponentMain />} />
                <Route path="/FaceCream" element={<FaceCreamComponentMain />} />
                <Route path="/Soaps" element={<ScrubComponentMain />} />
                {/* The Soaps page used to be Scrubs: old links and bookmarks still land on it. */}
                <Route path="/Scrubs" element={<Navigate to="/Soaps" replace />} />
                <Route path="/Signup" element={<SignUp />} />
                { (isLoggedIn || !authChecked) && <Route path="/Settings" element={account(<Settings />)} />}
                {/* Signed out, the Orders page is an order lookup (order number + email). */}
                <Route path="/Orders" element={account(<MyOrders />)} />
                {/* Write/EditPost/EditShop are admin-only: the underlying APIs enforce this
                    too, so this is just keeping the UI from ever offering a page that would
                    fail on submit. */}
                { (isAdmin || !authChecked) && (
                <>
                <Route path="/Write" element={account(<Write />)} />
                <Route path="/EditPost" element={account(<EditPost />)} />
                <Route path="/EditShop" element={account(<EditShop />)} />
                </>
                )}
                <Route path="/About" element={<About />} />
                <Route path="/Contact" element={<Contact />} />
                {/* The key makes switching between the two load the other page afresh. */}
                <Route path="/Privacy" element={<LegalPage key="privacy" page="privacy" />} />
                <Route path="/Terms" element={<LegalPage key="terms" page="terms" />} />
                <Route path="/Shop" element={<Shop />} />
                <Route path="/checkout" element={<PaymentForm />}/>
                <Route path="/payment-success" element={<SuccessPayement />} />
                <Route path="/DynamicPosts" element={<DynamicPosts />} />
                <Route path="/DynamicShop/:slug" element={<DynamicShop />} />
            </Routes>
        </Suspense>
        </>
    )
}
